import { useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { setCacheUser } from "./idb-cache";

export interface SessionUser {
  id: string;
  name: string | null;
  email: string;
  business_name: string | null;
  role: "user" | "admin";
  plan: "free" | "pro";
  subscription_status: "trial" | "active" | "past_due" | "suspended" | "cancelled";
  trial_ends_at: string | null;
  onboarding_completed: number;
  parent_account_id: string | null;
  team_role: string | null;
  // 'admin' = full access (mirrors owner). 'employee' = clock-in only.
  // null for the literal owner of the account.
  account_role: "admin" | "employee" | null;
  team_permissions: string[];
  // true when the owner explicitly chose a permission subset on the
  // invite form (or later edited it). false when the column is still
  // NULL — privileged accounts in that state inherit the full owner
  // nav as they always have.
  team_permissions_set: boolean;
  // The effective plan for this user's ACCOUNT — for owners that's
  // their own `plan`; for team members it's the parent owner's plan
  // (since team members are written with plan='free' at invite time
  // regardless of the parent's tier). Use this for Pro-only feature
  // gates so privileged team members on a Pro account aren't blocked.
  account_plan: "free" | "pro";
  // null until the owner finishes (or dismisses) the Pro setup wizard.
  // AppLayout uses this to auto-redirect new Pro users to /app/pro-setup.
  pro_setup_completed_at: string | null;
  // Per-employee feature flags. Owner sets these from the Employees
  // control center. Default (column default) is 1 = enabled, so existing
  // accounts behave the same as before this column was added.
  feat_breaks: number;
  feat_customer_contact: number;
  feat_time_off: number;
  // When 1, employee home swaps the live clock card for a "Add hours
  // manually" form (date + hours + job + notes).
  feat_manual_hours: number;
}

interface SessionResponse {
  user: SessionUser | null;
  impersonating: { realUserId: string; expiresAt: number } | null;
}

// Last session the server actually confirmed.
//
// Module-scoped on purpose: every page renders its own <AppLayout>, so
// navigating remounts this hook and its useState starts empty. Keeping
// the last confirmed answer out here is what lets a transient fetch
// failure fall back to something true instead of to "signed out".
//
// Safe against stale identity because every sign-out path does a full
// page load (see signOut below), which discards module state.
let lastConfirmedSession: SessionResponse | null = null;

export function clearSessionCache(): void {
  lastConfirmedSession = null;
}

export function useSession() {
  // Deliberately NOT seeded from lastConfirmedSession.
  //
  // Seeding it looked like a free optimization but broke sign-in: the
  // /signin page itself calls useSession(), which confirms {user:null}
  // into the cache. SignIn then client-navigates to /app, AppLayout
  // mounted with that stale "signed out" value AND loading already
  // false, and its redirect effect bounced the user straight back to
  // the sign-in form.
  //
  // So the cache is strictly a FAILURE fallback, used only in the catch
  // below. Every mount still blocks on a real fetch, exactly as before.
  const [data, setData] = useState<SessionResponse | undefined>();
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api<SessionResponse>("/api/auth/session");
      lastConfirmedSession = res;
      setData(res);
      // Tell the IDB cache layer who owns the data — partitions cache
      // by user_id so signing in as a different person doesn't leak
      // the previous person's leads/jobs into the offline view.
      setCacheUser(res.user?.id ?? null);
    } catch {
      // A throw means we could not ASK — network blip, timeout, or a
      // 5xx. It does not mean signed out: an expired, missing or
      // invalid token still comes back 200 with {user:null}.
      //
      // This used to set {user:null}, which AppLayout reads as signed
      // out and redirects to /signin. Since every navigation remounts
      // AppLayout and refetches, a single failed request mid-navigation
      // threw a still-signed-in user out to the login page. That is the
      // "it kicks me out sometimes" report.
      //
      // So fall back to the last confirmed session and leave the cache
      // owner alone — clearing it would drop the offline view for
      // someone who is still signed in. Only a cold start with nothing
      // confirmed resolves to null, which is the genuinely-signed-out
      // first load.
      setData(lastConfirmedSession ?? { user: null, impersonating: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { user: data?.user ?? null, impersonating: data?.impersonating ?? null, loading, refresh };
}

// Crew-mode lets ANY user (owner, privileged team member, or actual
// employee) flip their UI into the field-crew dashboard for on-site
// work. Owners use it to clock in / log hours / send "on my way" without
// the full business nav in the way. Persists to localStorage and pings
// every subscriber on the same tab via a custom event (the native
// `storage` event only fires across tabs).
const CREW_MODE_KEY = "fk_crew_mode";
const CREW_MODE_EVENT = "fk:crew-mode";

export function useCrewMode(): [boolean, (next: boolean) => void] {
  const [on, setOn] = useState<boolean>(
    () => typeof window !== "undefined" && localStorage.getItem(CREW_MODE_KEY) === "1",
  );
  useEffect(() => {
    function sync() {
      setOn(localStorage.getItem(CREW_MODE_KEY) === "1");
    }
    window.addEventListener(CREW_MODE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CREW_MODE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  function setCrewMode(next: boolean) {
    if (next) localStorage.setItem(CREW_MODE_KEY, "1");
    else localStorage.removeItem(CREW_MODE_KEY);
    window.dispatchEvent(new Event(CREW_MODE_EVENT));
  }
  return [on, setCrewMode];
}

export async function signOut() {
  await api("/api/auth/signout", { method: "POST" });
  // Belt-and-braces: the redirect below is a full page load, which
  // discards module state anyway, but don't leave a confirmed session
  // sitting around in case this ever becomes a client-side navigation.
  clearSessionCache();
  // Wipe any service-worker-cached account data so a different user signing
  // in on the same device can't read it back offline.
  try {
    navigator.serviceWorker?.controller?.postMessage("fk:clear-cache");
  } catch {
    /* best-effort */
  }
  window.location.href = "/";
}
